import { createSelector, createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import dayjs from 'dayjs'
import jwtDecode from 'jwt-decode'

import {
  DriedVegetablesValues,
  FishFrequencyValues,
  FruitsValues,
  MeatFrequencyValues,
  NutritionalWeight,
  StarchyFoodsValues,
  SugarFrequencyValues,
} from '../../types'
import type { RootState } from '../store'
import {
  NotificationFrequency,
  Objectives,
  PlanStructure,
  Preferences,
  Recipe,
  User,
  UserPlan,
  UserPushPreferences,
} from '../../types/fam-graphql'
import {
  getServiceState,
  getServiceReducers,
  ServiceValues,
} from '../../helpers/ReduxHelpers'
import { reorderPlan } from '../../models/Planning/reorderPlan'

import { actionTypes } from './types'

export const name = 'user'

export type UserState = Omit<User, 'plans'> & {
  isInit: boolean
  pushPreferences: UserPushPreferences | null
  nutritionalPreferences: { [key: string]: any } | null
  isGeneratingPlanning: boolean
  generationMessage: string | null
  updateObjectives: ServiceValues
  updatePreferences: ServiceValues
  updatePushPreferences: ServiceValues
  keycloakToken: string | null
  isKeycloakProcessDone: boolean
  notificationPreferences: {
    shouldReceiveNotifications: boolean
    notificationFrequency: NotificationFrequency
  }
  updateNotification: ServiceValues
  hasJustModifiedExpressPref: 'onboard' | 'update' | null
  plans: UserPlan[] | null
  hasDownloadMenu: boolean
}
//
// Initial state
//

const initialState: UserState = {
  isInit: false,
  id: '',
  preferences: null,
  objectives: null,
  token: null,
  plans: null,
  current_plan: null,
  favorite_recipes: null,
  pushPreferences: {
    installIOS: false,
    installAndroid: false,
    notification: false,
  },
  //
  nutritionalPreferences: null,
  isGeneratingPlanning: false,
  generationMessage: null,
  //
  updateObjectives: getServiceState(),
  updatePreferences: getServiceState(),
  updatePushPreferences: getServiceState(),
  keycloakToken: null,
  isKeycloakProcessDone: false,
  notificationPreferences: {
    shouldReceiveNotifications: false,
    notificationFrequency: NotificationFrequency.Daily,
  },
  updateNotification: getServiceState(),
  hasJustModifiedExpressPref: null,
  hasDownloadMenu: false,
}

//
// Slice (Actions & Reducers)
//

const updateObjectivesReducers = getServiceReducers('updateObjectives')
const updatePreferencesReducers = getServiceReducers('updatePreferences')
const updateNotificationReducers = getServiceReducers('updateNotification')
const updatePushPreferencesReducers = getServiceReducers(
  'updatePushPreferences'
)
const updateDownloadPreferencesReducers = getServiceReducers(
  'updateDownloadPreferences'
)

const slice = createSlice({
  name,
  initialState,
  reducers: {
    init: () => undefined,
    setIsInit: (state, action: actionTypes.setIsInit) => {
      state.isInit = action?.payload
    },
    setUser: (state, action: actionTypes.setUser) => {
      state.id = action.payload?.id || ''
      // set token only if user = null or payload has a token
      // keep current token otherwise
      if (!action.payload || !!action.payload?.token) {
        state.token = action.payload?.token
      }
      state.preferences = action.payload?.preferences
      state.objectives = action.payload?.objectives
      if (action.payload?.plans || !action.payload) {
        // only if payload has plans or payload = null
        state.plans = action.payload?.plans?.data || []
      }
      if (action.payload?.current_plan || !action.payload) {
        // only if payload has current_plan or payload = null
        state.current_plan = action.payload?.current_plan
      }
      state.favorite_recipes = action.payload?.favorite_recipes
      state.notificationPreferences = {
        shouldReceiveNotifications:
          action.payload?.should_receive_notifications || false,
        notificationFrequency:
          action.payload?.notification_frequency || NotificationFrequency.Daily,
      }
      state.pushPreferences = {
        installIOS: action.payload?.push_preferences?.installIOS || false,
        installAndroid:
          action.payload?.push_preferences?.installAndroid || false,
        notification: action.payload?.push_preferences?.notification || false,
      }
      state.hasDownloadMenu = action.payload?.has_download_menu || false
    },
    setPlans: (state, action: actionTypes.setPlans) => {
      state.plans = action.payload
    },
    setToken: (state, action: actionTypes.setToken) => {
      state.token = action.payload
    },
    setKeycloakToken: (state, action: actionTypes.setKeycloakToken) => {
      state.token = action.payload
      state.keycloakToken = action.payload
    },
    setIsKeycloakProcessDone: (
      state,
      action: actionTypes.setIsKeycloakProcessDone
    ) => {
      state.isKeycloakProcessDone = action.payload
    },
    generatePlanning: () => undefined,
    refreshUser: () => undefined,
    updateObjectivesRequest: updateObjectivesReducers.request,
    updateObjectivesSuccess: updateObjectivesReducers.success,
    updateObjectivesError: updateObjectivesReducers.error,
    updateObjectivesReset: updateObjectivesReducers.reset,
    updatePreferencesRequest: updatePreferencesReducers.request,
    updatePreferencesSuccess: updatePreferencesReducers.success,
    updatePreferencesError: updatePreferencesReducers.error,
    updatePreferencesReset: updatePreferencesReducers.reset,
    setCurrentPlan: (state, action: actionTypes.setCurrentPlan) => {
      state.current_plan = action.payload
    },
    setIsGeneratingPlanning: (
      state,
      action: actionTypes.setIsGeneratingPlanning
    ) => {
      if (state.isGeneratingPlanning !== action.payload) {
        window.scrollTo(0, 0)
      }
      state.isGeneratingPlanning = action.payload
    },
    setGenerationMessage: (state, action: actionTypes.setGenerationMessage) => {
      state.generationMessage = action.payload
    },
    setNutritionalPreferences: (
      _state,
      _action: actionTypes.setNutritionalPreferences
    ) => void null,
    setFavoriteRecipes: (state, action: actionTypes.setFavoriteRecipes) => {
      state.favorite_recipes = action.payload
    },
    updateFavoriteRecipes: (
      _state,
      _action: actionTypes.updateFavoriteRecipes
    ) => void null,
    addRecipeToFavorites: (_state, _action: actionTypes.addRecipeToFavorites) =>
      void null,
    removeRecipeFromFavorites: (
      _state,
      _action: actionTypes.removeRecipeFromFavorites
    ) => void null,
    setNotificationPreferences: (
      state,
      action: actionTypes.setNotificationPreferences
    ) => {
      state.notificationPreferences = action.payload
    },
    updateNotificationRequest: updateNotificationReducers.request,
    updateNotificationSuccess: updateNotificationReducers.success,
    setPushPreferences: (state, action: actionTypes.setPushPreferences) => {
      state.pushPreferences = action.payload
    },
    updatePushPreferencesRequest: updatePushPreferencesReducers.request,
    sendSelfNotification: () => undefined,
    setHasJustModifiedExpressPref: (
      state,
      action: actionTypes.setHasJustModifiedExpressPref
    ) => {
      state.hasJustModifiedExpressPref = action.payload
    },
    updateDownloadPreferencesRequest: updateDownloadPreferencesReducers.request,
    updateDownloadPreferencesSuccess: updateDownloadPreferencesReducers.success,
    setHasDownloadMenu: (state, action: actionTypes.setHasDownloadMenu) => {
      state.hasDownloadMenu = action.payload
    },
  },
  extraReducers: {
    [HYDRATE]: (state) => {
      // do not overwrite user state
      return state
    },
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[name]
const isGeneratingPlanning = (state: RootState) =>
  root(state).isGeneratingPlanning
const generationMessage = (state: RootState) => root(state).generationMessage
const currentPlan = (state: RootState) => root(state).current_plan

const planning = createSelector([currentPlan as any], (plan: UserPlan) => {
  // Generate a week of date time
  return plan
    ? Array(7)
        .fill(dayjs(plan.start_at, 'YYYY-MM-DD').format('YYYY-MM-DD'))
        .map((date, idx) =>
          dayjs(date, 'YYYY-MM-DD').add(idx, 'days').format('YYYY-MM-DD')
        )
    : null
})

const preferences = (state: RootState) => root(state).preferences
const objectives = (state: RootState) => root(state).objectives

const nutritionalPreferences = createSelector(
  [preferences as any, objectives as any],
  (prefs: Preferences, objs: Objectives) => {
    const transformStructure = (structure: string | PlanStructure) => {
      const mapping: any = {
        ENT_PLAT: 'ENT_PLAT',
        ENT_PLAT_DESSERT: 'ENT_PLAT_DESSERT',
        PLAT_DESSERT: 'PLAT_DESSERT',
      }
      return mapping?.[structure] || 'ENT_PLAT'
    }
    const transformExpress = (express?: boolean | null) =>
      express ? true : false
    const transformFruits = (fruits: number) =>
      [
        FruitsValues.PORTIONS_3,
        FruitsValues.PORTIONS_4,
        FruitsValues.PORTIONS_5,
      ]?.[fruits - 1] || FruitsValues.PORTIONS_3
    const transformStarchy = (starchy: number) =>
      [
        StarchyFoodsValues.PER_WEEK_1,
        StarchyFoodsValues.PER_WEEK_3,
        StarchyFoodsValues.PER_WEEK_7,
      ]?.[starchy - 1] || StarchyFoodsValues.PER_WEEK_1
    const transformDried = (dried: DriedVegetablesValues) =>
      dried || DriedVegetablesValues.RARELY
    const transformMeat = (meat: number) =>
      [
        MeatFrequencyValues.PER_WEEK_5,
        MeatFrequencyValues.PER_WEEK_3,
        MeatFrequencyValues.PER_WEEK_2,
      ]?.[meat - 1] || MeatFrequencyValues.PER_WEEK_5
    const transformFish = (meat: number) =>
      [
        FishFrequencyValues.RARELY,
        FishFrequencyValues.PER_WEEK_1,
        FishFrequencyValues.PER_WEEK_2,
      ]?.[meat - 1] || FishFrequencyValues.RARELY
    const transformSugar = (sugar: SugarFrequencyValues) =>
      sugar || SugarFrequencyValues.PER_DAY_2

    return (
      objs &&
      prefs &&
      ({
        people_count: prefs?.portions ?? 2,
        diet_practice: prefs?.regime,
        course_composition: transformStructure(prefs?.structure as string),
        express: transformExpress(prefs?.express),
        fruits_vegetables_quantity: transformFruits(
          objs?.fruit_and_vegetable as number
        ),
        starchy_foods_frequency: transformStarchy(
          objs?.complete_carbohydrate as number
        ),
        dried_vegetables_frequency: transformDried(
          objs?.dried_vegetable as number
        ),
        meat_frequency: transformMeat(objs?.meat as number),
        fish_frequency: transformFish(objs?.fish as number),
        drink_frequency: transformSugar(objs?.sugary_product as number),
        moment: prefs?.moment,
      } as any)
    )
  }
)

const canImproveNutritionalPreferences = (_nutritionalSteps: any) => {
  return createSelector([nutritionalPreferences], (userPrefs) => {
    const answersWeight = _nutritionalSteps.map((s: any) => {
      return s?.options.find((o: any) => o.id === userPrefs?.[s.name ?? ''])
        ?.weight
    })

    return (
      answersWeight?.includes(NutritionalWeight.MINIMAL) ||
      answersWeight?.includes(NutritionalWeight.MEDIUM)
    )
  })
}

const canImpairNutritionalPreferences = (_nutritionalSteps: any) => {
  return createSelector([nutritionalPreferences], (userPrefs) => {
    const answersWeight = _nutritionalSteps.map((s: any) => {
      const option = s?.options.find(
        (o: any) => o.id === userPrefs?.[s.name ?? '']
      )
      return option ? option?.weight : s?.options[0].weight
    })

    return (
      answersWeight?.includes(NutritionalWeight.OPTIMAL) ||
      answersWeight?.includes(NutritionalWeight.MEDIUM)
    )
  })
}

const improvedObjectivesList = (_nutritionalSteps: any) => {
  return createSelector([nutritionalPreferences], (userPrefs) => {
    const OBJECTIVES_ORDER = [
      NutritionalWeight.MINIMAL,
      NutritionalWeight.MEDIUM,
      NutritionalWeight.OPTIMAL,
    ]

    const improvedObjectives = _nutritionalSteps
      .map((s: any) => {
        const fromIndex = s?.options.findIndex(
          (o: any) => o.id === userPrefs?.[s.name ?? '']
        )

        if (s?.disabled) {
          return null
        }

        return {
          name: s?.name ?? '',
          label: s?.title ?? '',
          currentObjective:
            s?.options.find((o: any) => o.id === userPrefs?.[s.name ?? '']) ??
            s?.options[0],
          improvedObjective:
            s?.options?.slice(fromIndex + 1, fromIndex + 2)?.[0] ?? null,
        }
      })
      .filter((e: any) => e?.improvedObjective)
      .sort(
        (a: any, b: any) =>
          OBJECTIVES_ORDER.indexOf(a?.currentObjective?.weight) -
          OBJECTIVES_ORDER.indexOf(b?.currentObjective?.weight)
      )

    const improvedObjectivesWithProposal = improvedObjectives.map((e: any) => {
      const proposal = `${e.name}_${e.currentObjective.weight}_to_${e.improvedObjective.weight}`
      return { ...e, proposal }
    })

    return improvedObjectivesWithProposal
  })
}

const isUserOnboarded = (state: RootState) => !!root(state).preferences

const hasValidPlan = createSelector(
  [planning],
  (pl) =>
    pl?.includes(dayjs().format('YYYY-MM-DD')) ||
    pl?.includes(dayjs().add(1, 'day').format('YYYY-MM-DD')) ||
    false
)

const favoriteRecipes = (state: RootState) => root(state)?.favorite_recipes

const favoriteRecipesIds = createSelector(
  [favoriteRecipes],
  (recipes?: Recipe[] | null) => recipes?.map((r: Recipe) => r?.id)
)

const token = (state: RootState) => root(state)?.token
const id = (state: RootState) => root(state)?.id
const keycloakToken = (state: RootState) => root(state)?.keycloakToken
const isKeycloakProcessDone = (state: RootState) =>
  root(state)?.isKeycloakProcessDone
const plans = (state: RootState) => root(state)?.plans
const isConnected = (state: RootState) => !!root(state)?.id
const jwt = createSelector([token], (tokenString) => {
  return tokenString && jwtDecode(tokenString)
})
const notificationPreferences = (state: RootState) =>
  root(state)?.notificationPreferences
const pushPreferences = (state: RootState) => root(state)?.pushPreferences
const updatePreferences = (state: RootState) => root(state)?.updatePreferences
const hasJustModifiedExpressPref = (state: RootState) =>
  root(state)?.hasJustModifiedExpressPref
const isInit = (state: RootState) => root(state).isInit
const currentPlanReordered = createSelector([currentPlan], (p) => {
  return reorderPlan(p)
})
const hasDownloadMenu = (state: RootState) => root(state).hasDownloadMenu

export const selectors = {
  preferences,
  objectives,
  currentPlan,
  currentPlanReordered,
  planning,
  isGeneratingPlanning,
  isUserOnboarded,
  nutritionalPreferences,
  canImproveNutritionalPreferences,
  canImpairNutritionalPreferences,
  hasValidPlan,
  improvedObjectivesList,
  generationMessage,
  token,
  id,
  jwt,
  isConnected,
  plans,
  favoriteRecipes,
  favoriteRecipesIds,
  keycloakToken,
  isKeycloakProcessDone,
  notificationPreferences,
  pushPreferences,
  updatePreferences,
  hasJustModifiedExpressPref,
  isInit,
  hasDownloadMenu,
}
